import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Box, Collapse, IconButton, Stack, TableCell, TableRow } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useDeleteAlertConfiguration } from '@/api/customSensorAlerts';
import { DeviceAlert, SensorAlertTypes } from '@/api/customSensorAlerts/types';
import { useDevice } from '@/api/devices';
import { useDisplayPreferences } from '@/api/userAccount';
import { Datum, formatDatum } from '@/components/data/Datum';
import { AlertReferenceChart } from '@/components/spotter/notificationTab/AlertReferenceChart';
import { AlertEditFormValues } from '@/components/spotter/notificationTab/CustomSensorAlertsSection';
import { SensorAlertEditForm } from '@/components/spotter/notificationTab/SensorAlertEditForm';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { triggerQuantityType } from '@/utils';

interface SensorAlertRowProps {
    row: DeviceAlert;
}

export const SensorAlertRow = ({ row }: SensorAlertRowProps) => {
    const { data: device } = useDevice();
    const { data: displayPreferences } = useDisplayPreferences();
    const { mutateAsync: deleteAlertConfiguration } = useDeleteAlertConfiguration();
    const { isOpen, toggle } = useDisclosure();
    const triggerType = row.trigger_details.trigger_type as SensorAlertTypes;

    const thresholdValue = useMemo(() => {
        if (!device || !displayPreferences) {
            return null;
        }
        return formatDatum({
            unitPreferences: displayPreferences,
            value: row.trigger_details.value,
            sourceUnit: row.trigger_details.units,
            type: triggerQuantityType(row.trigger_details),
            hideUnit: true,
            noGrouping: true,
            context: device,
        });
    }, [device, displayPreferences, row.trigger_details]);

    const formMethods = useForm<AlertEditFormValues>({
        defaultValues: {
            alertType: triggerType,
            logicalOperator: row.trigger_details.operator ?? '>',
            thresholdValue: thresholdValue ?? row.trigger_details.value?.toString() ?? '',
            sensorPosition: row.trigger_details.sensorPosition?.toString() ?? '',
        },
        mode: 'onChange',
    });
    useUnsavedChangesGuard(formMethods.formState.isDirty, `sensor-alert-${row.id}`);

    useEffect(() => {
        if (thresholdValue) {
            formMethods.reset({
                alertType: triggerType,
                logicalOperator: row.trigger_details.operator ?? '>',
                thresholdValue: thresholdValue,
                sensorPosition: row.trigger_details.sensorPosition?.toString() ?? '',
            });
        }
    }, [formMethods, row.trigger_details.operator, row.trigger_details.sensorPosition, thresholdValue, triggerType]);

    const onDelete = async () => {
        if (!device) {
            return;
        }

        try {
            await deleteAlertConfiguration({
                spotterId: device.spotterId,
                alertId: `${row.id}`,
                alertType: row.trigger_details.displayName,
                details: `${row.trigger_details.operator} ${row.trigger_details.value} ${row.trigger_details.units} ${row.trigger_details.sensorPosition}`,
                sensorPosition: row.trigger_details.sensorPosition ?? null,
            });
        } catch (err) {
            console.error(err);
        }
    };

    const onFormReset = () => {
        formMethods.reset();
    };

    return (
        <>
            <TableRow sx={{ '&& > *': { borderBottom: 'unset' } }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }} align="center">
                    {row.trigger_details.name}
                </TableCell>
                <TableCell align="center">
                    {row.trigger_details.operator}{' '}
                    <Datum
                        value={Number(row.trigger_details.value)}
                        sourceUnit={row.trigger_details.units}
                        context={device}
                        type={triggerQuantityType(row.trigger_details)}
                    />
                </TableCell>
                <TableCell align="center">
                    {typeof row.trigger_details.sensorPosition === 'number' && row.trigger_details.sensorPosition > 0
                        ? row.trigger_details.sensorPosition
                        : 'Spotter'}
                </TableCell>
                <TableCell align="right">
                    <IconButton onClick={toggle}>
                        {isOpen ? <ExpandLessIcon color="warning" /> : <EditIcon color="warning" />}
                    </IconButton>
                </TableCell>
                <TableCell align="right">
                    <IconButton onClick={onDelete}>
                        <DeleteForeverIcon color="error" />
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <FormProvider {...formMethods}>
                            <Stack
                                direction="row"
                                sx={{
                                    flexWrap: { xs: 'wrap-reverse', md: 'nowrap' },
                                    gap: 2,
                                    justifyContent: 'center',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexGrow: 1, maxWidth: 'calc(min(100vw - 100px, 500px))' }}>
                                    <AlertReferenceChart enabled={isOpen} alertType={triggerType} />
                                </Box>
                                <SensorAlertEditForm
                                    isEditFlow
                                    isOpen={isOpen}
                                    close={onDelete}
                                    onReset={onFormReset}
                                />
                            </Stack>
                        </FormProvider>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};
